import React, { FC, useEffect, useState } from 'react';

import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button, Descriptions, Modal, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';

import EditModal from './EditModal';
import { optionsRegMetki, updateDurationIntervals } from '../../../../helpers/utils/data';
import { displayLanguageByKey } from '../../../../helpers/utils/ui';
import MainLayout from '../../../../shared/common/components/layouts/MainLayout';
import PageHead from '../../../../shared/common/components/page-header/PageHeader';
import { routeNames } from '../../../../shared/common/constants/routes';
import { useActions } from '../../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';


const SchoolEdit: FC = () => {
  const navigate = useNavigate();
  const [ editModalVisible, setEditModalVisible ] = useState(false);

  const { isLoading, schoolDetail, selectedSchool } =
    useTypedSelector((state) => state.currentSchool);
  const {
    currentSchoolFetchDetails,
    currentSchoolSetUpdate,
    currentSchoolDelete,
    fetchUserAndSchoolInfo,
    setAppReportOpen,
    setDrawerLink,
  } = useActions();

  const instructionBtn = (
    <span
      style={{ marginLeft: 10, marginRight: 10, marginTop: 3, cursor: 'pointer' }}
      onClick={() => {
        try {
          setDrawerLink('https://doc.scananalytics.ru/nastrojka/nastrojki-shkoly/');
          setAppReportOpen(true);
        } catch (err) {
          console.error(err);
        }
      }}
    >
      <QuestionCircleOutlined
        style={{ color: '#ccc', fontSize: 14, marginTop: '8px', marginLeft: '-10px' }}
      />
    </span>
  );


  useEffect(() => {
    if (selectedSchool) {
      currentSchoolFetchDetails(selectedSchool.id);
      currentSchoolSetUpdate(false);
    }
  }, [ selectedSchool?.id ]);

  const removeCurrentSchool = () => {
    if (selectedSchool) {
      currentSchoolDelete(selectedSchool.id, () => {
        fetchUserAndSchoolInfo(navigate, () => {
          navigate(routeNames.schoolAdd);
        });
      });
    }
  };

  const showConfirm = () => {
    Modal.confirm({
      title: 'Вы уверены что хотите удалить школу?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Да',
      okType: 'danger',
      cancelText: 'Нет',
      onOk() {
        removeCurrentSchool();
      },
    });
  };

  // Функция для корректного отображения UTM полей
  const displayUtmField = (value?: string | null) =>
    value && value.trim() !== '' ? value : '-';

  return (
    <MainLayout pageTitle="Настройка школы">
      <div className="page-wrapper">
        <PageHead
          title="Настройка школы"
          extraContent={instructionBtn}
          extra={
            <div>
              <Button
                data-help-id="settings-school-edit"
                icon={<EditOutlined />}
                type="primary"
                loading={isLoading}
                disabled={!schoolDetail}
                onClick={() => setEditModalVisible(true)}
                style={{ marginRight: 5 }}
              />
              <Button
                data-help-id="settings-school-delete"
                icon={<DeleteOutlined />}
                loading={isLoading}
                onClick={showConfirm}
                danger
              />
            </div>
          }
        />

        <div className="page-content">
          {isLoading ? (
            <div
              className="global-loading-container"
              style={{ height: '60vh' }}
            >
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
              />
            </div>
          ) : (
            <Descriptions
              data-help-id="settings-school-info"
              title=""
              bordered
              column={1}
              size="middle"
            >
              <Descriptions.Item label="Название школы" labelStyle={{ minWidth: 300 }} contentStyle={{ minWidth: '60%' }}>
                {schoolDetail?.name || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Валюта">
                {schoolDetail?.currency?.name || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Язык интерфейса">
                {displayLanguageByKey(schoolDetail?.language?.name || '-')}
              </Descriptions.Item>

              <Descriptions.Item label="Часовой пояс">
                {schoolDetail?.timezone || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Домен школы геткурса">
                {schoolDetail?.domain || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Основной домен школы">
                {schoolDetail?.main_domain || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Последнее обновление">
                {schoolDetail?.last_update || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Частота обновления данных">
                {updateDurationIntervals.find(el => el.id === schoolDetail?.update_duration)?.name || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Откуда брать UTM метки регистраций">
                {optionsRegMetki.find(el => el.id === schoolDetail?.utm_settings?.utm_origin)?.name || '-'}
              </Descriptions.Item>

              <Descriptions.Item label="Доп. поле для utm_source">
                {displayUtmField(schoolDetail?.utm_settings?.utm_source)}
              </Descriptions.Item>

              <Descriptions.Item label="Доп. поле для utm_medium">
                {displayUtmField(schoolDetail?.utm_settings?.utm_medium)}
              </Descriptions.Item>

              <Descriptions.Item label="Доп. поле для utm_campaign">
                {displayUtmField(schoolDetail?.utm_settings?.utm_campaign)}
              </Descriptions.Item>

              <Descriptions.Item label="Доп. поле для utm_content">
                {displayUtmField(schoolDetail?.utm_settings?.utm_content)}
              </Descriptions.Item>

              <Descriptions.Item label="Доп. поле для utm_term">
                {displayUtmField(schoolDetail?.utm_settings?.utm_term)}
              </Descriptions.Item>

              <Descriptions.Item label="Доп. поле для utm_group">
                {displayUtmField(schoolDetail?.utm_settings?.utm_group)}
              </Descriptions.Item>
            </Descriptions>
          )}
        </div>

        <EditModal
          isVisible={editModalVisible}
          setVisible={setEditModalVisible}
          initialData={schoolDetail}
        />
      </div>
    </MainLayout>
  );
};

export default SchoolEdit;
